import { tryOnScopeDispose } from '@vueuse/core';
import { EffectScope } from '@vue/composition-api';
import {
  effectScope,
  getCurrentInstance,
  getCurrentScope,
  inject,
  provide,
  watch
} from '@vue/composition-api';
import { EdgeChange, FlowProps, NodeChange, WorkFlowStore } from '../types';
// import { ErrorCode, WorkFlowError, warn } from '../utils'
import { WorkFlow } from '../context';
import { Storage } from '../utils/storage';

type Injection = WorkFlowStore | null | undefined;
type Scope = (EffectScope & { workFlowId: string }) | undefined;

// todo: maybe replace the storage with a context based solution; This would break calling useWorkFlow outside a setup function though, which should be fine
/**
 * Composable that provides access to a store instance
 *
 * If no id is provided, the store instance is injected from context
 *
 * If no store instance is found in context, a new store instance is created and registered in storage
 *
 * @public
 * @returns a vue flow store instance
 * @param idOrOpts - id of the store instance or options to create a new store instance
 */
export function useWorkFlow(id?: string): WorkFlowStore;
export function useWorkFlow(options?: FlowProps): WorkFlowStore;
export function useWorkFlow(idOrOpts?: any): WorkFlowStore {
  const storage = Storage.getInstance();

  const scope = getCurrentScope() as Scope;

  const isOptsObj = typeof idOrOpts === 'object';

  const options = isOptsObj ? idOrOpts : { id: idOrOpts };

  const id = options.id;
  const workFlowId = id ?? scope?.workFlowId;

  let workFlow: Injection;

  /**
   * check if we can get a store instance through injections
   * this should be the regular way after initialization
   */
  if (scope) {
    const injectedState = inject(WorkFlow, null);
    if (
      typeof injectedState !== 'undefined' &&
      injectedState !== null &&
      (!workFlowId || injectedState!.id === workFlowId)
    ) {
      workFlow = injectedState;
    }
  }

  /**
   * check if we can get a store instance through storage
   * this requires options id or an id on the current scope
   */
  if (!workFlow) {
    if (workFlowId) {
      workFlow = storage.get(workFlowId);
    }
  }

  /**
   * If we cannot find any store instance in the previous steps
   * _or_ if the store instance we found does not match up with provided ids
   * create a new store instance and register it in storage
   */
  if (!workFlow || (workFlowId && workFlow.id !== workFlowId)) {
    const name = id ?? storage.getId();

    const state = storage.create(name, options);

    workFlow = state;

    const vfScope = scope ?? effectScope(true);
    vfScope.run(() => {
      /**
       * We have to watch the applyDefault option here,
       * because we need to register the default hooks before the `WorkFlow` component is actually mounted and props passed
       * Otherwise calling `addNodes` while the component is not mounted will not trigger any changes unless change handlers are explicitly bound
       */
      watch(
        state.applyDefault,
        (shouldApplyDefault, __, onCleanup) => {
          const nodesChangeHandler = (changes: NodeChange[]) => {
            state.applyNodeChanges(changes);
          };

          const edgesChangeHandler = (changes: EdgeChange[]) => {
            state.applyEdgeChanges(changes);
          };

          if (shouldApplyDefault) {
            state.onNodesChange(nodesChangeHandler);
            state.onEdgesChange(edgesChangeHandler);
          } else {
            state.hooks.value.nodesChange.off(nodesChangeHandler);
            state.hooks.value.edgesChange.off(edgesChangeHandler);
          }

          // Release handlers on cleanup
          onCleanup(() => {
            state.hooks.value.nodesChange.off(nodesChangeHandler);
            state.hooks.value.edgesChange.off(edgesChangeHandler);
          });
        },
        { immediate: true }
      );

      // Destroy store instance on scope dispose
      tryOnScopeDispose(() => {
        if (workFlow) {
          const storedInstance = storage.get(workFlow.id);

          if (storedInstance) {
            storedInstance.$destroy();
          } else {
            console.warn(
              `No store instance found for id ${workFlow.id} in storage.`
            );
          }
        }
      });
    });
  } else {
    // If options were passed, overwrite state with the options' values
    if (isOptsObj) {
      workFlow.setState(options);
    }
  }

  // Provide a fresh instance into context if we are in a scope
  if (scope) {
    provide(WorkFlow, workFlow);

    scope.workFlowId = workFlow!.id;
  }

  if (isOptsObj) {
    const instance = getCurrentInstance();

    // ignore the warning if we are in a WorkFlow component
    if (instance?.type.name !== 'WorkFlow') {
      // workFlow.emits.error(new WorkFlowError(ErrorCode.USEVUEFLOW_OPTIONS))
    }
  }

  return workFlow as WorkFlowStore;
}
